import * as React from 'react'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import FieldSelector from 'modules/reports/components/Editor/Carcasa/FormFields/FieldSelector'
import type { LabelType } from 'modules/reports/components/Editor/Carcasa/FormFields/FieldSelector'
import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import { useLoadingFields } from 'modules/reports/contexts/loadFieldsContext'
import { useValidationContext } from 'modules/reports/contexts/validationContext'
import useFields from 'modules/reports/hooks/useFields'
import { FieldSelectionType, useReportTracking } from 'modules/reports/hooks/useReportTracking'
import useValidateProjections from 'modules/reports/hooks/useValidateProjection'
import type { ReportEditorStore } from 'modules/reports/lib/Store'
import type Field from 'modules/reports/store/models/Field'
import type { AbilityType } from 'modules/reports/store/models/types'

const EMPTY_ARRAY: string[] = []

interface Props {
  store: ReportEditorStore
  fieldAbilityTypes?: AbilityType[]
  excludeIdentifiers?: boolean
  excludeTypes?: string[]
  uiGroup?: string
  label: LabelType
  selectionType: FieldSelectionType
  onRemove?: (fieldType: FieldType) => void
  onClickItem?: (fields: Field[]) => void
  onClickButton?: () => void
}

const createStoreRef = createComputed((newStore: ReportEditorStore) => {
  return newStore
})

const MultiProjectionField = ({
  store,
  fieldAbilityTypes,
  excludeIdentifiers,
  excludeTypes,
  uiGroup,
  label,
  selectionType,
  onRemove,
  onClickItem,
  onClickButton,
}: Props) => {
  const { trackFieldChange } = useReportTracking(store)
  const newStore = createStoreRef(store)
  const [, setLoadingFields] = useLoadingFields()
  const [, setValidation] = useValidationContext()
  const { validateProjection } = useValidateProjections()
  const loadFields = useFields()

  const loadFieldRelationship = useUnsafeCallback(() => {
    loadFields(
      { projections: newStore.get().projection.paths() },
      (status) => setLoadingFields(status),
      (status) => setLoadingFields(status)
    )
  }, [loadFields, newStore, setLoadingFields])

  const onRemoveField = useUnsafeCallback(
    async (fieldType: FieldType) => {
      if (onRemove) {
        onRemove(fieldType)
      }

      newStore.get().projection.removeField(fieldType)

      const result = handleResult(
        await validateProjection({
          projections: newStore.get().projection.paths(),
          filters: newStore.get().filters.paths(),
        }),
        () => undefined
      )
      if (result.success && result.data.validateProjection) {
        setValidation(result.data.validateProjection)
      }

      loadFieldRelationship()
    },
    [onRemove, newStore, loadFieldRelationship, setValidation, validateProjection]
  )

  const onSelectFields = useUnsafeCallback(
    (fields: Field[]) => {
      trackFieldChange(selectionType)

      newStore.get().projection.addFieldsWithGroup(fields, uiGroup)
      loadFieldRelationship()
    },
    [selectionType, newStore, uiGroup, trackFieldChange, loadFieldRelationship]
  )

  const onReorderFields = useUnsafeCallback(
    (fields: FieldType[]) => {
      newStore.get().projection.reorderFields(fields)
      loadFieldRelationship()
    },
    [newStore, loadFieldRelationship]
  )

  const onCloseFields = useUnsafeCallback(() => {
    loadFieldRelationship()
  }, [loadFieldRelationship])

  const fieldTypes = newStore.get().projection.selectedFieldTypesByUiGroup(uiGroup)

  const excludePaths = newStore.get().projection.excludedFieldPathsFromUiGroup(uiGroup)

  return (
    <FieldSelector
      onClickButton={onClickButton}
      onClickItem={onClickItem}
      variables={EMPTY_ARRAY}
      selectMultiple
      fieldTypes={fieldTypes}
      excludePaths={excludePaths}
      fieldAbilityTypes={fieldAbilityTypes}
      chartType={store.reportStruct.type}
      label={label}
      onSelectFields={onSelectFields}
      onRemoveField={onRemoveField}
      onReorderFields={onReorderFields}
      excludeIdentifiers={excludeIdentifiers}
      excludeTypes={excludeTypes}
      buttonLabel={i18n.t('custom_reports.fields.table.initial_data.button')}
      modalTitle={i18n.t('custom_reports.fields.initial_data')}
      modalDescription={i18n.t('custom_reports.fields.select_initial_data_description')}
      onCloseModal={onCloseFields}
    />
  )
}

export default observer(MultiProjectionField)
